package pl.decks.personal.pages.ievp.sections.skills

import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import pl.decks.personal.components.widgets.iconribbon.IconRibbonStylesheet
import pl.decks.personal.components.widgets.mode.ModeInputStylesheet
import pl.decks.personal.components.widgets.progression.RampingProgressionBarStylesheet
import pl.decks.personal.components.widgets.ranks.RanksStylesheet
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout

object SkillsSectionStylesheet {
    object Vars {
        val systemSelectionBorderRadius by StyleVariable<CSSLengthOrPercentageNumericValue>(1.em)
        val profileDimensionColor by StyleVariable<Color>()
    }

    object SystemSelection {
        val Style = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.XXSmall)
        }
        val Title = FontTypography.serif.extendedByBase {
            Modifier.fontSize(FontTypography.Size.Small).color(colorMode.toSitePalette().accents.secondary)
        }

        val ModeInput = CssStyle {
            val sitePalette = colorMode.toSitePalette()
            base {
                Modifier.fillMaxWidth().backgroundColor(sitePalette.surface)
            }
            hover {
                Modifier.setVariable(ModeInputStylesheet.Vars.borderColor, sitePalette.accents.secondary)
            }
        }

        sealed interface InputOptionKind : ComponentKind

        val InputOption = CssStyle<InputOptionKind> {
            val accent = colorMode.toSitePalette().accents.secondary.toRgb()
            hover {
                Modifier.setVariable(ModeInputStylesheet.Vars.backgroundColor, accent.copyf(alpha = 0.15f))
            }
            active {
                Modifier.setVariable(ModeInputStylesheet.Vars.backgroundColor, accent.copyf(alpha = 0.25f))
            }
        }
        val InputOptionSelectedVariant = InputOption.addVariantBase {
            val accent = colorMode.toSitePalette().accents.secondary
            Modifier
                .fontWeight(FontWeight.Medium)
                .setVariable(ModeInputStylesheet.Vars.textColor, accent)
        }
        val InputOptionOtherVariant = InputOption.addVariantBase {
            Modifier
                .fontWeight(FontWeight.Normal)
                .setVariable(ModeInputStylesheet.Vars.textColor, colorMode.toPalette().color)
        }

        val SlotsPresentation = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .overflow(Overflow.Clip)
                .backgroundColor(colorMode.toSitePalette().surface)
                .borderRadius(
                    bottomLeft = Vars.systemSelectionBorderRadius.value(),
                    bottomRight = Vars.systemSelectionBorderRadius.value(),
                )
                .border(
                    width = ModeInputStylesheet.Vars.borderWidth.value(),
                    style = LineStyle.Solid,
                    color = ModeInputStylesheet.Vars.borderColor.value(colorMode.toPalette().border),
                )
                .borderTop(width = SiteLayout.Line.none)
        }
    }

    object RanksSlotsContainer {
        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            Modifier
                .fillMaxWidth()
                .overflow(Overflow.Hidden)
                .setVariable(RanksStylesheet.Vars.appearDuration, TransitionDurationVars.Slow.value())
                .setVariable(RanksStylesheet.Vars.appearPositionDelay, TransitionDurationVars.Fast.value())
                .transition {
                    property("max-height", "opacity")
                    duration(TransitionDurationVars.Slow.value())
                    timingFunction(TransitionTimingFunction.EaseInOut)
                }
        }
        val CollapsedVariant = Style.addVariantBase { Modifier.maxHeight(0.px).opacity(0) }
        val ExpandedVariant = Style.addVariantBase { Modifier.maxHeight(300.px).opacity(1) }
    }

    object ProfileDimensions {
        val Preview = CssStyle {
            base {
                Modifier
                    .fillMaxWidth()
                    .gap(SiteLayout.Space.XSmall)
                    .setVariable(IconRibbonStylesheet.Vars.size, 40.px)
                    .setVariable(ResponsiveGridStylesheet.Vars.gridGap, SiteLayout.Space.XXSmall)
                    .setVariable(ResponsiveGridStylesheet.Vars.borderRadius, SiteLayout.Rounding.Medium)
                    .setVariable(ResponsiveGridStylesheet.Vars.focusColor, Vars.profileDimensionColor.value())
                    .setVariable(ResponsiveGridStylesheet.Vars.focusBorderColor, Vars.profileDimensionColor.value())
                    .setVariable(RampingProgressionBarStylesheet.Vars.fillColor, Vars.profileDimensionColor.value())
            }

            Breakpoint.MD {
                Modifier.setVariable(IconRibbonStylesheet.Vars.size, 50.px)
            }
        }

        val Category = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.XSmall)
        }

        val Skills = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.XXSmall)
        }

        val SkillBar = CssStyle.base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.XXXSmall)
        }

        val SkillTitle = FontTypography.mono.extendedByBase {
            Modifier
                .fillMaxWidth()
                .textAlign(TextAlign.End)
                .fontSize(FontTypography.Size.Small)
                .fontWeight(FontWeight.Medium)
        }
    }

    val Page = CssStyle.base {
        val accents = colorMode.toSitePalette().accents
        Modifier
            .gap(SiteLayout.Space.XSmall)
            .setVariable(RanksStylesheet.Vars.titleColor, accents.primary)
            .setVariable(RanksStylesheet.Vars.levelColor, accents.secondary)
    }
}
